import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Input, Form, message, Tag } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const AnnouncementsAdmin = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const [editRecord, setEditRecord] = useState(null);

  const [formAdd] = Form.useForm();
  const [formEdit] = Form.useForm();

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/dashboard/announcements");
      setAnnouncements(res.data.announcements || []);
    } catch (err) {
      message.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const submitAdd = async () => {
    try {
      const values = await formAdd.validateFields();

      await axiosInstance.post("/dashboard/announcements", values);

      message.success("Announcement added");
      formAdd.resetFields();
      setOpenAddModal(false);
      fetchAnnouncements();
    } catch (err) {
      message.error("Failed to add");
    }
  };

  const openEdit = (record) => {
    setEditRecord(record);
    formEdit.setFieldsValue({
      title: record.title,
      description: record.description,
      type: record.type,
    });
    setOpenEditModal(true);
  };

  const submitEdit = async () => {
    try {
      const values = await formEdit.validateFields();

      await axiosInstance.put(
        `/dashboard/announcements/${editRecord.id}`,
        values
      );

      message.success("Announcement updated");
      setOpenEditModal(false);
      fetchAnnouncements();
    } catch (err) {
      message.error("Failed to update");
    }
  };

  const deleteAnnouncement = (id) => {
    Modal.confirm({
      title: "Delete Announcement?",
      icon: <DeleteOutlined />,
      onOk: async () => {
        try {
          await axiosInstance.delete(`/dashboard/announcements/${id}`);
          message.success("Deleted");
          fetchAnnouncements();
        } catch (err) {
          message.error("Failed to delete");
        }
      },
    });
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      render: (t) => <strong className="text-gray-800">{t}</strong>,
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (d) => <span className="text-gray-600">{d}</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (type) => {
        const color =
          type === "success"
            ? "green"
            : type === "warning"
            ? "orange"
            : "blue";
        return <Tag color={color}>{type}</Tag>;
      },
    },
    {
      title: "Date",
      dataIndex: "time",
      render: (t) => (
        <span className="text-gray-500">
          {new Date(t).toISOString().split("T")[0]}
        </span>
      ),
    },
    {
      title: "Actions",
      width: 200,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
            className="border-blue-500 text-blue-600"
          >
            Edit
          </Button>

          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteAnnouncement(record.id)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row justify-between gap-4">

            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                <NotificationOutlined /> Manage Announcements
              </h1>
              <p className="text-gray-600">Create and edit important updates</p>
            </div>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setOpenAddModal(true)}
              className="bg-blue-600 hover:bg-blue-700 border-blue-600 h-10 px-4"
            >
              Add Announcement
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <Table
            dataSource={announcements}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 6 }}
          />
        </div>
      </div>

      {/* ADD MODAL */}
      <Modal
        title="Add Announcement"
        open={openAddModal}
        onCancel={() => setOpenAddModal(false)}
        footer={null}
      >
        <Form layout="vertical" form={formAdd} onFinish={submitAdd}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="type" label="Type" initialValue="info">
            <Input />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setOpenAddModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Add
            </Button>
          </div>
        </Form>
      </Modal>

      {/* EDIT MODAL */}
      <Modal
        title="Edit Announcement"
        open={openEditModal}
        onCancel={() => setOpenEditModal(false)}
        footer={null}
      >
        <Form layout="vertical" form={formEdit} onFinish={submitEdit}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Input />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AnnouncementsAdmin;
